import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader } from './../../components/card/card.jsx';
import SmallPentestDocumentation from './SmallPentestDocumentation';

const Documentation = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSections, setFilteredSections] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');

  const documentationSections = [
    {
      id: 'overview',
      title: 'Overview',
      icon: 'bi bi-house',
      content: {
        title: 'GuardPoint Cyber Security Platform',
        description: 'Welcome to GuardPoint — a comprehensive cybersecurity platform to assess, monitor, and improve your organization’s security posture.',
        features: [
          'Automated penetration testing (Small Pentest)',
          'AI‑driven vulnerability analysis and risk prioritization',
          'Real‑time threat monitoring and environment health',
          'Reporting and analytics for engineers and leadership',
          'User management and role‑based access control (RBAC)',
          'Job scheduler and recurring checks (Cron Tests)',
          'Integrations via API and webhooks',
          'Audit logs and change history'
        ]
      }
    },
    {
      id: 'small-pentest',
      title: 'Small Pentest',
      icon: 'bi bi-shield-check',
      content: <SmallPentestDocumentation />
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'bi bi-play-circle',
      content: {
        title: 'Getting Started with GuardPoint',
        description: 'Quick, safe setup to start using GuardPoint effectively.',
        steps: [
          {
            title: 'Sign in and roles',
            description: 'Sign in with your account and verify you have the right role (User, Superuser, Admin) for the tasks you need.'
          },
          {
            title: 'Dashboard overview',
            description: 'Review your overall security status, latest checks, and key metrics on the main dashboard.'
          },
          {
            title: 'Prepare the environment',
            description: 'Verify network reachability of targets, prepare a test zone, and ensure your company policies permit scanning.'
          },
          {
            title: 'First scan (Small Pentest)',
            description: 'Open Small Pentest, enter a domain or IP, and run in safe mode to minimize impact.'
          },
          {
            title: 'Review results',
            description: 'Study reports, identified vulnerabilities, and remediation recommendations. Capture an action plan.'
          },
          {
            title: 'What’s next',
            description: 'Use AI Chat for clarifications, Agents for automation, and Cron Tests to schedule regular checks.'
          }
        ]
      }
    },
    {
      id: 'features',
      title: 'Features',
      icon: 'bi bi-gear',
      content: {
        title: 'Platform Features',
        description: 'Explore the comprehensive features available in GuardPoint.',
        features: [
          {
            name: 'Small Pentest',
            description: 'Automated penetration testing for quick security assessments',
            icon: 'bi bi-cpu'
          },
          {
            name: 'AI Chat',
            description: 'Interactive AI assistant for security guidance and analysis',
            icon: 'bi bi-robot'
          },
          {
            name: 'Agents',
            description: 'Manage and deploy security agents across your infrastructure',
            icon: 'bi bi-people'
          },
          {
            name: 'User Management',
            description: 'Control user access and permissions with role-based security',
            icon: 'bi bi-people-fill'
          },
          {
            name: 'Statistics & Analytics',
            description: 'Comprehensive reporting and analytics dashboard',
            icon: 'bi bi-bar-chart-fill'
          }
        ]
      }
    },
    {
      id: 'security-testing',
      title: 'Security Testing',
      icon: 'bi bi-shield-check',
      content: {
        title: 'Security Testing Guide',
        description: 'How to use GuardPoint’s security testing capabilities effectively and safely.',
        sections: [
          {
            title: 'Approach & safety',
            content: 'We follow a safety‑first approach: conservative defaults, minimal impact on production, and alignment with internal policies.'
          },
          {
            title: 'Types of checks',
            content: 'Network and service discovery, baseline web checks, safe Active Directory reconnaissance, and OSINT collection.'
          },
          {
            title: 'Profiles & tags',
            content: 'Use profiles and tags to group checks by targets, criticality, and ownership.'
          },
          {
            title: 'Speed & limits',
            content: 'Tune rate limits, timeouts, and depth to meet SLOs and avoid disrupting production systems.'
          },
          {
            title: 'Artifacts & reports',
            content: 'Results include artifacts and full logs; reports provide prioritized remediation guidance.'
          },
          {
            title: 'Practice recommendations',
            content: 'Start in a test environment, validate on small scope, then schedule regular checks via Cron Tests.'
          }
        ]
      }
    },
    {
      id: 'ai-features',
      title: 'AI Features',
      icon: 'bi bi-robot',
      content: {
        title: 'AI-Powered Security',
        description: 'Leverage AI to interpret results, prioritize work, and accelerate decision‑making.',
        capabilities: [
          {
            name: 'Intelligent analysis',
            description: 'Detects patterns across findings, correlates data, and estimates risk.'
          },
          {
            name: 'Automated recommendations',
            description: 'Generates remediation steps tailored to context and best practices.'
          },
          {
            name: 'Interactive chat',
            description: 'Ask questions, get report explanations, and build action plans via AI Chat.'
          },
          {
            name: 'Threat Intelligence',
            description: 'Enriches findings with external context and highlights active campaigns.'
          },
          {
            name: 'Log summarization',
            description: 'Condenses long scan logs into concise briefs with key highlights.'
          },
          {
            name: 'Data normalization',
            description: 'Brings outputs from different agents to a uniform format for comparison.'
          }
        ]
      }
    },
    {
      id: 'ai-chat',
      title: 'AI Chat',
      icon: 'bi bi-robot',
      content: {
        title: 'AI Chat Assistant',
        description: 'Ask security questions, analyze findings, and get step-by-step guidance via an interactive chat interface with Markdown and code block support.',
        steps: [
          'Open AI > Chat and click New Conversation to start fresh',
          'Type your prompt (e.g., “Analyze these scan results” or “Suggest a remediation plan”) and press Enter',
          'Use Regenerate to try a different answer or Stop to halt a long response',
          'Manage conversations in the sidebar: rename, delete, or switch between threads'
        ],
        highlights: [
          'Live streaming responses with typing indicator',
          'Markdown rendering with code blocks and tables',
          'Conversation history and quick switching',
          'Copy-friendly answers and stable formatting'
        ]
      }
    },
    {
      id: 'agents',
      title: 'Agents',
      icon: 'bi bi-gear-wide-connected',
      content: {
        title: 'Agents Manager',
        description: 'Run and monitor backend jobs for reconnaissance, testing, and utilities. Stream logs, track status, and review results with powerful filters.',
        steps: [
          'Select an agent, then set Target and the required Options',
          'Click Start and use Stream to follow logs in real time',
          'Open a job to see detailed status and results after completion',
          'Use Cancel to stop a running job and clean up temporary data',
          'Use filters and sorting to quickly find historical runs'
        ],
        notes: [
          'Custom agents are supported: create/edit CPAI YAML in the built‑in editor',
          'Endpoints: /jobs/start, /jobs/status/{id}, /jobs/results, /jobs/stream',
          'Role‑based access: starting/cancelling may require Superuser/Admin permissions',
          'Security: avoid storing secrets in YAML — prefer env variables and secure vaults'
        ]
      }
    },
    {
      id: 'ai-tools',
      title: 'AI Tools',
      icon: 'bi bi-cpu',
      content: {
        title: 'AI Tools & Profiles',
        description: 'Run guided, AI‑driven profiles (e.g., Safe AD Recon) to perform structured, safe assessments with clear outputs.',
        profiles: [
          { name: 'Safe AD Recon', description: 'Collects safe reconnaissance data about Active Directory without intrusive actions' },
          { name: 'Safe', description: 'Read‑only checks with minimal footprint' }
        ],
        actions: [
          'Set targets and options; use Dry Run to preview the plan without execution',
          'Run Apply Plan to execute steps with progress tracking and logs',
          'Tune timers and limits to meet SLOs and maintenance windows'
        ]
      }
    },
    {
      id: 'orchestrations',
      title: 'Orchestrations',
      icon: 'bi bi-diagram-3',
      content: {
        title: 'Security Orchestrations',
        description: 'Automate multi-step security workflows. Review orchestration lists, summaries, and environment health at a glance.',
        sections: [
          { title: 'Orchestration List', content: 'Browse available orchestrations and their current state' },
          { title: 'Summaries & Health', content: 'Refresh summaries to view progress, status, and recent outcomes' },
          { title: 'Network/Tor Status', content: 'Check connectivity indicators and status before running workflows' }
        ]
      }
    },
    {
      id: 'cron-tests',
      title: 'Cron Tests',
      icon: 'bi bi-clock-history',
      content: {
        title: 'Scheduled Security Scans',
        description: 'Create and manage recurring security tasks. Admin and Superuser roles only.',
        steps: [
          'Create a task with a cron schedule, target, and options',
          'Toggle enable/disable or trigger a run immediately',
          'Review execution history, statuses, and output logs'
        ],
        notes: [
          'Use clear naming and safe options for production environments',
          'Failed runs are surfaced in history and can be retried manually'
        ]
      }
    },
    {
      id: 'user-management',
      title: 'User Management',
      icon: 'bi bi-people',
      content: {
        title: 'User Management',
        description: 'Manage users, roles, passwords, and MFA. Actions are audited and require proper permissions.',
        actions: [
          'Search and filter users; open a user to edit',
          'Update roles and permissions; save changes',
          'Reset MFA when needed (user will re-enroll)',
          'Set a temporary password or force reset on next login',
          'Delete users who no longer require access'
        ]
      }
    },
    {
      id: 'user-roles',
      title: 'User Roles & Permissions',
      icon: 'bi bi-person-badge',
      content: {
        title: 'User Roles and Permissions',
        description: 'Understanding the different user roles and their capabilities within GuardPoint.',
        roles: [
          {
            name: 'Admin',
            description: 'Full system access including user management, statistics, and all security features',
            permissions: ['All features', 'User management', 'System configuration', 'Analytics access']
          },
          {
            name: 'Superuser',
            description: 'Advanced user with access to agent management and most security features',
            permissions: ['Security testing', 'Agent management', 'AI chat', 'Report viewing']
          },
          {
            name: 'User',
            description: 'Standard user with access to basic security testing and analysis features',
            permissions: ['Small pentest', 'AI chat', 'Basic reporting', 'Documentation']
          }
        ]
      }
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: 'bi bi-tools',
      content: {
        title: 'Troubleshooting Guide',
        description: 'Common issues and their solutions.',
        issues: [
          {
            problem: 'Scan fails to start',
            solution: 'Verify target accessibility and check network connectivity. Ensure proper permissions are set.'
          },
          {
            problem: 'AI chat not responding',
            solution: 'Check internet connectivity and refresh the page. Contact support if the issue persists.'
          },
          {
            problem: 'Cannot access certain features',
            solution: 'Verify your user role and permissions. Contact an administrator if you need additional access.'
          },
          {
            problem: 'Reports not generating',
            solution: 'Ensure scans have completed successfully. Check system resources and try again.'
          }
        ]
      }
    },
    {
      id: 'api-reference',
      title: 'API Reference',
      icon: 'bi bi-code-square',
      content: {
        title: 'API Reference',
        description: 'Technical documentation for developers integrating with GuardPoint.',
        endpoints: [
          {
            method: 'POST',
            endpoint: '/api/scans',
            description: 'Initiate a new security scan'
          },
          {
            method: 'GET',
            endpoint: '/api/scans/{id}',
            description: 'Retrieve scan results'
          },
          {
            method: 'GET',
            endpoint: '/api/reports',
            description: 'List available reports'
          },
          {
            method: 'POST',
            endpoint: '/api/auth/login',
            description: 'Authenticate user'
          }
        ]
      }
    }
  ];

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredSections(documentationSections);
    } else {
      const filtered = documentationSections.filter(section => {
        const searchLower = searchQuery.toLowerCase();
        return (
          section.title.toLowerCase().includes(searchLower) ||
          JSON.stringify(section.content).toLowerCase().includes(searchLower)
        );
      });
      setFilteredSections(filtered);
    }
  }, [searchQuery]);

  const renderContent = (section) => {
    const { content } = section;
    
    switch (section.id) {
      case 'overview':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            <h5>Key Features:</h5>
            <ul className="list-unstyled">
              {content.features.map((feature, index) => (
                <li key={index} className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        );
      
      case 'small-pentest':
        return content;
      
      case 'getting-started':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.steps.map((step, index) => (
              <div key={index} className="mb-4">
                <h6 className="text-primary">
                  <span className="badge bg-primary me-2">{index + 1}</span>
                  {step.title}
                </h6>
                <p className="ms-4">{step.description}</p>
              </div>
            ))}
          </div>
        );
      
      case 'features':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            <div className="row">
              {content.features.map((feature, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <div className="d-flex align-items-start">
                    <i className={`${feature.icon} text-primary me-3 mt-1`}></i>
                    <div>
                      <h6 className="mb-1">{feature.name}</h6>
                      <p className="text-muted small mb-0">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'security-testing':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.sections.map((section, index) => (
              <div key={index} className="mb-4">
                <h6 className="text-primary">{section.title}</h6>
                <p>{section.content}</p>
              </div>
            ))}
          </div>
        );
      
      case 'ai-features':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.capabilities.map((capability, index) => (
              <div key={index} className="mb-3">
                <h6 className="mb-1">{capability.name}</h6>
                <p className="text-muted">{capability.description}</p>
              </div>
            ))}
          </div>
        );
      
      case 'ai-chat':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.steps && (
              <div className="mb-4">
                <h6 className="text-primary">How to use</h6>
                {content.steps.map((step, index) => (
                  <div key={index} className="d-flex align-items-start mb-2">
                    <span className="badge bg-primary me-2">{index + 1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}
            {content.highlights && (
              <div>
                <h6 className="text-primary">Highlights</h6>
                <ul className="list-unstyled mb-0">
                  {content.highlights.map((h, i) => (
                    <li key={i} className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      
      case 'agents':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.steps && (
              <div className="mb-4">
                <h6 className="text-primary">Quick start</h6>
                <ul className="list-unstyled">
                  {content.steps.map((s, i) => (
                    <li key={i} className="mb-2">
                      <i className="bi bi-check2-square text-primary me-2"></i>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {content.notes && (
              <div className="alert alert-info mb-0">
                <div className="d-flex">
                  <i className="bi bi-info-circle me-2 mt-1"></i>
                  <div>
                    <div className="fw-bold mb-1">Notes</div>
                    <ul className="mb-0 ps-3">
                      {content.notes.map((n, i) => (
                        <li key={i}>{n}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'ai-tools':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.profiles && (
              <div className="mb-4">
                <h6 className="text-primary">Profiles</h6>
                <div className="row">
                  {content.profiles.map((p, i) => (
                    <div key={i} className="col-md-6 mb-3">
                      <div className="d-flex align-items-start">
                        <i className="bi bi-cpu text-primary me-3 mt-1"></i>
                        <div>
                          <h6 className="mb-1">{p.name}</h6>
                          <p className="text-muted small mb-0">{p.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {content.actions && (
              <div>
                <h6 className="text-primary">How it works</h6>
                <ul className="list-unstyled mb-0">
                  {content.actions.map((a, i) => (
                    <li key={i} className="mb-2">
                      <i className="bi bi-arrow-right-circle text-primary me-2"></i>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      
      case 'orchestrations':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.sections && content.sections.map((sec, i) => (
              <div key={i} className="mb-3">
                <h6 className="text-primary mb-1">{sec.title}</h6>
                <p className="text-muted mb-0">{sec.content}</p>
              </div>
            ))}
          </div>
        );
      
      case 'cron-tests':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.steps && (
              <div className="mb-3">
                <h6 className="text-primary">Workflow</h6>
                {content.steps.map((s, i) => (
                  <div key={i} className="d-flex align-items-start mb-2">
                    <span className="badge bg-secondary me-2">{i + 1}</span>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            )}
            {content.notes && (
              <div className="alert alert-warning mb-0">
                <div className="d-flex">
                  <i className="bi bi-shield-exclamation me-2 mt-1"></i>
                  <div>
                    <div className="fw-bold mb-1">Recommendations</div>
                    <ul className="mb-0 ps-3">
                      {content.notes.map((n, i) => (
                        <li key={i}>{n}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'user-management':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.actions && (
              <div>
                <h6 className="text-primary">Common actions</h6>
                <ul className="list-unstyled mb-0">
                  {content.actions.map((a, i) => (
                    <li key={i} className="mb-2">
                      <i className="bi bi-person-check text-primary me-2"></i>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      
      case 'user-roles':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.roles.map((role, index) => (
              <div key={index} className="mb-4">
                <h6 className="text-primary">{role.name}</h6>
                <p className="mb-2">{role.description}</p>
                <div className="ms-3">
                  <strong>Permissions:</strong>
                  <ul className="mt-1">
                    {role.permissions.map((permission, pIndex) => (
                      <li key={pIndex}>{permission}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        );
      
      case 'troubleshooting':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            {content.issues.map((issue, index) => (
              <div key={index} className="mb-4">
                <h6 className="text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {issue.problem}
                </h6>
                <div className="ms-4">
                  <strong className="text-success">Solution:</strong>
                  <p className="mt-1">{issue.solution}</p>
                </div>
              </div>
            ))}
          </div>
        );
      
      case 'api-reference':
        return (
          <div>
            <p className="mb-4">{content.description}</p>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {content.endpoints.map((endpoint, index) => (
                    <tr key={index}>
                      <td>
                        <span className={`badge ${
                          endpoint.method === 'GET' ? 'bg-success' : 
                          endpoint.method === 'POST' ? 'bg-primary' : 
                          endpoint.method === 'PUT' ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {endpoint.method}
                        </span>
                      </td>
                      <td><code>{endpoint.endpoint}</code></td>
                      <td>{endpoint.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      default:
        return <p>{content.description}</p>;
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        {/* Sidebar Navigation */}
        <div className="col-lg-3 col-md-4">
          <Card className="mb-4">
            <CardHeader>
              <h5 className="mb-0">
                <i className="bi bi-book me-2"></i>
                Documentation
              </h5>
            </CardHeader>
            <CardBody className="p-0">
              {/* Search */}
              <div className="p-3 border-bottom">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search documentation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Navigation Menu */}
              <div className="list-group list-group-flush">
                {filteredSections.map((section) => (
                  <button
                    key={section.id}
                    className={`list-group-item list-group-item-action d-flex align-items-center ${
                      activeSection === section.id ? 'active' : ''
                    }`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <i className={`${section.icon} me-2`}></i>
                    {section.title}
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
        
        {/* Main Content */}
        <div className="col-lg-9 col-md-8">
          <Card>
            <CardBody>
              {(() => {
                const currentSection = filteredSections.find(s => s.id === activeSection);
                if (!currentSection) {
                  return (
                    <div className="text-center py-5">
                      <i className="bi bi-search display-1 text-muted"></i>
                      <h4 className="mt-3">No results found</h4>
                      <p className="text-muted">Try adjusting your search terms</p>
                    </div>
                  );
                }
                
                return (
                  <div>
                    <div className="d-flex align-items-center mb-4">
                      <i className={`${currentSection.icon} text-primary me-3 fs-3`}></i>
                      <h2 className="mb-0">{currentSection.content.title}</h2>
                    </div>
                    {renderContent(currentSection)}
                  </div>
                );
              })()}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Documentation;